/* -----------------------------------------------------------------------------
   The marketing banner, set on the S8MC Website Config screen.

   ⚠ REBUILT 8 Oct to match the agreed mockup — the `.feature` card in the
   "Web Ordering Overhaul" artifact (claude.ai/artifact/RT7gobCZmY5g8aXT4zisNe).
   The first version was a thin one-line strip above the whole header, built
   from the spec's wording without opening the mockup. The mockup is:

     - INSIDE the menu column, above the first category — not above the header
     - a rounded card about 168px tall, on a brand-colour gradient with a soft
       highlight, text sitting bottom-left
     - a HEADLINE in the display face, a smaller line of copy beneath it, and a
       white pill button

   Where the mockup and the spec disagree, the mockup wins — it is the design
   Jono signed off.

   ── Headline and body, from one field ──────────────────────────────────────
   The database holds one `BannerCopy` field. Until it grows separate headline
   and button-text columns, the FIRST LINE of the copy is the headline and
   anything after it is the smaller line — and the S8MC screen says so. If
   `bannerHeadline` / `bannerCtaText` ever arrive on the response they are used
   instead, so adding those columns needs no change here.

   ── When it renders nothing ────────────────────────────────────────────────
   Not enabled, nothing to show, or no config at all — and it returns NULL in
   each case, not an empty element, which would still take its margin and leave
   an unexplained gap above the menu.
   --------------------------------------------------------------------------- */

const DEFAULT_CTA = "Find out more";

export default function MarketingBanner(props) {
    const config = props.websiteConfig;
    if (!config || config.bannerIsEnabled !== true) { return null; }

    const { headline, body } = splitCopy(config);
    const imageUrl = config.bannerURL || "";
    if (headline === "" && body === "" && imageUrl === "") { return null; }

    const hasText = headline !== "" || body !== "";
    const link = config.bannerLinkURL || "";
    const cta = (config.bannerCtaText || "").trim() || DEFAULT_CTA;

    /* With an uploaded image, the image is the background and a scrim keeps the
       text readable on any photograph. Without one, the brand gradient from
       theme.css does the work, exactly as the mockup draws it. */
    const style = imageUrl !== "" ? { backgroundImage: `url("${cssUrl(imageUrl)}")` } : undefined;
    const className = "wo-banner"
        + (imageUrl !== "" ? " wo-banner--image" : "")
        + (!hasText ? " wo-banner--imageOnly" : "")
        + (link ? " wo-banner--link" : "");

    const content = hasText &&
        <div className="wo-banner__text">
            {headline !== "" && <h3 className="wo-banner__head">{headline}</h3>}
            {body !== "" && <p className="wo-banner__body">{body}</p>}
            {/* The button only exists when there is somewhere to go. A pill that
                looks tappable and does nothing is worse than no pill. */}
            {link && <span className="wo-banner__cta">{cta}</span>}
        </div>;

    /* The whole card is the link, not just the pill — a bigger target on a phone,
       and the pill is the visual cue. ⚠ rel="noopener noreferrer": the href comes
       from a database row, and target="_blank" without noopener hands the opened
       page a handle on ours whatever the server validated. */
    if (link) {
        return (
            <a className={className} style={style} href={link}
               target="_blank" rel="noopener noreferrer"
               aria-label={headline || body || cta}>
                {content}
            </a>
        );
    }

    return (
        <div className={className} style={style}>
            {content}
        </div>
    );
}

/* Headline + body. Explicit fields win; otherwise first line / the rest. */
function splitCopy(config) {
    const explicitHead = (config.bannerHeadline || "").trim();
    const copy = (config.bannerCopy || "").replace(/\r\n/g, "\n").trim();

    if (explicitHead !== "") {
        return { headline: explicitHead, body: copy };
    }
    const nl = copy.indexOf("\n");
    if (nl === -1) {
        return { headline: copy, body: "" };
    }
    return {
        headline: copy.slice(0, nl).trim(),
        body: copy.slice(nl + 1).replace(/\s*\n\s*/g, " ").trim()
    };
}

/* The URL goes inside a CSS url("…"), so a quote or backslash in it would end
   the string early. It is a server-composed image URL, but escaping costs
   nothing and makes that a non-question. */
function cssUrl(url) {
    return String(url).replace(/["\\\n\r]/g, (c) => "\\" + c);
}
